import { PROTOCOL_VERSION } from '/shared/protocol.js';

let inputSeq = 0;
let lastSnapshotTick = 0;

let wsSocket = null;
let connectionToken = 0;

const listeners = new Map();
const TEXT_DECODER = new TextDecoder();

function makeWsUrl() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

function emit(type, payload) {
  const handlers = listeners.get(type);
  if (!handlers || handlers.size === 0) return;
  for (const handler of handlers) {
    try {
      handler(payload);
    } catch (err) {
      console.error(`network listener failed for "${type}"`, err);
    }
  }
}

function on(type, handler) {
  if (!listeners.has(type)) {
    listeners.set(type, new Set());
  }
  listeners.get(type).add(handler);
  return () => {
    listeners.get(type)?.delete(handler);
  };
}

function directHandleMessage(raw) {
  let message;
  try {
    message = JSON.parse(raw);
  } catch (_) {
    return;
  }
  if (message?.t === 's' && Number.isFinite(Number(message.simTick))) {
    lastSnapshotTick = Number(message.simTick);
  }
  emit('message', message);
  if (typeof message?.t === 'string') {
    emit(message.t, message);
  }
}

function directSafeSend(payload) {
  if (!wsSocket || wsSocket.readyState !== WebSocket.OPEN) return false;
  try {
    wsSocket.send(JSON.stringify(payload));
    return true;
  } catch (error) {
    emit('error', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
}

function nextConnectionToken() {
  connectionToken += 1;
  return connectionToken;
}

function isCurrentConnection(token) {
  return token === connectionToken;
}

function handleWebSocketMessage(data) {
  if (typeof data === 'string') {
    directHandleMessage(data);
    return;
  }
  if (data instanceof ArrayBuffer) {
    directHandleMessage(TEXT_DECODER.decode(new Uint8Array(data)));
    return;
  }
  if (ArrayBuffer.isView(data)) {
    directHandleMessage(TEXT_DECODER.decode(data));
    return;
  }
  if (typeof data?.text === 'function') {
    data.text().then(directHandleMessage).catch(() => { });
  }
}

async function connect({
  roomId = 'default',
  playerName = 'Player',
  sessionToken = null,
  password = ''
} = {}) {
  disconnect();
  const token = connectionToken;
  const socket = new WebSocket(makeWsUrl());
  socket.binaryType = 'arraybuffer';

  return await new Promise((resolve, reject) => {
    let settled = false;
    let wsClosed = false;

    socket.addEventListener('open', () => {
      if (!isCurrentConnection(token)) {
        settled = true;
        try { socket.close(); } catch (_) { }
        reject(new Error('Connection attempt was superseded'));
        return;
      }
      wsSocket = socket;
      settled = true;
      directSafeSend({
        t: 'hello',
        v: PROTOCOL_VERSION,
        room: roomId,
        name: playerName,
        ...(typeof sessionToken === 'string' && sessionToken ? { token: sessionToken } : {}),
        // Only ever on a join that has to prove itself. A token-backed
        // reconnect carries none, and neither does an unlocked room.
        ...(typeof password === 'string' && password ? { password } : {})
      });
      emit('open', { roomId, playerName, transport: 'websocket', mode: 'direct' });
      resolve();
    });

    socket.addEventListener('message', (event) => {
      if (!isCurrentConnection(token)) return;
      handleWebSocketMessage(event.data);
    });

    socket.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      try { socket.close(); } catch (_) { }
      const error = new Error('WebSocket connection failed');
      if (isCurrentConnection(token)) {
        if (wsSocket === socket) wsSocket = null;
        emit('error', error);
      }
      reject(error);
    });

    socket.addEventListener('close', () => {
      if (wsClosed) return;
      wsClosed = true;
      const current = isCurrentConnection(token);
      const wasActive = current && wsSocket === socket;
      if (!settled) {
        settled = true;
        const error = new Error('WebSocket connection closed before it opened');
        if (current) {
          emit('error', error);
        }
        reject(error);
        return;
      }
      if (wasActive) {
        wsSocket = null;
        emit('close', {});
      }
    });
  });
}

function sendInput(left, right, options = {}) {
  const seq = Number.isFinite(Number(options.seq))
    ? (Number(options.seq) & 0xffff)
    : (inputSeq++ & 0xffff);
  const payload = {
    t: 'i',
    seq,
    clientTime: Number.isFinite(Number(options.clientTime)) ? Number(options.clientTime) : Date.now(),
    left: Boolean(left) ? 1 : 0,
    right: Boolean(right) ? 1 : 0,
    lastSnapTick: Number.isFinite(Number(options.lastSnapTick))
      ? Number(options.lastSnapTick)
      : lastSnapshotTick,
    ...(Number.isFinite(Number(options.applyServerMs))
      ? { applyServerMs: Math.max(0, Math.trunc(Number(options.applyServerMs))) }
      : {})
  };
  directSafeSend(payload);
  return seq;
}

function sendPing(id = Date.now(), clientTime = Date.now()) {
  directSafeSend({
    t: 'p',
    id,
    clientTime
  });
}

function sendChat(text) {
  if (typeof text !== 'string') return false;
  const normalized = text.trim();
  if (!normalized) return false;
  directSafeSend({ t: 'c', text: normalized });
  return true;
}

function sendChatDelete(id) {
  const normalized = typeof id === 'string' ? id.trim() : '';
  if (!normalized) return false;
  directSafeSend({ t: 'cd', id: normalized });
  return true;
}

function sendColorSelect(colorId, botIndex = null) {
  const id = Number.isInteger(colorId) ? colorId : -1;
  return directSafeSend({
    t: 'sc',
    colorId: id,
    ...(Number.isInteger(botIndex) && botIndex >= 0 ? { botIndex } : {})
  });
}

function sendTrailOpacitySelect(trailOpacityPercent, botIndex = null) {
  const percent = Number.isFinite(Number(trailOpacityPercent))
    ? Math.round(Number(trailOpacityPercent))
    : 100;
  return directSafeSend({
    t: 'so',
    trailOpacityPercent: percent,
    ...(Number.isInteger(botIndex) && botIndex >= 0 ? { botIndex } : {})
  });
}

function sendTurnRadiusSelect(turnRadiusPercent) {
  const percent = Number.isFinite(Number(turnRadiusPercent))
    ? Math.round(Number(turnRadiusPercent))
    : 86;
  return directSafeSend({ t: 'tr', turnRadiusPercent: percent });
}

function sendSetTeam(team) {
  return directSafeSend({ t: 'st', team });
}

function sendSpectate(spectate) {
  return directSafeSend({ t: 'sp', spectate: spectate ? 1 : 0 });
}

function sendCancelCountdown() {
  return directSafeSend({ t: 'cc' });
}

function sendStopMatch() {
  return directSafeSend({ t: 'sm' });
}

function requestReset() {
  directSafeSend({ t: 'r' });
}

function sendBye() {
  directSafeSend({ t: 'bye' });
}

function togglePause() {
  directSafeSend({ t: 'u' });
}

function setLastSnapshotTick(tick) {
  if (!Number.isFinite(Number(tick))) return;
  lastSnapshotTick = Number(tick);
}

function disconnect() {
  nextConnectionToken();
  const socket = wsSocket;
  wsSocket = null;
  if (socket) {
    try { socket.close(); } catch (_) { }
  }
}

export {
  connect,
  disconnect,
  on,
  requestReset,
  togglePause,
  sendBye,
  sendChat,
  sendChatDelete,
  sendColorSelect,
  sendTrailOpacitySelect,
  sendTurnRadiusSelect,
  sendSetTeam,
  sendSpectate,
  sendCancelCountdown,
  sendStopMatch,
  sendInput,
  sendPing,
  setLastSnapshotTick
};
